/**
 * LogTransports - Handles sending logs to the console or remote servers.
 */

import { LogEntry, LogLevel, LOG_STYLES } from './LogTypes';
import { LogSanitizer } from './LogSanitizer';
import {
  isSafeLogUserId,
  redactLogString,
} from '../../shared/logging/LogRedaction';

export interface Transport {
  send(entry: LogEntry): void;
}

export class ConsoleTransport implements Transport {
  private nativeConsole: Console;

  constructor(nativeConsole: Console) {
    this.nativeConsole = nativeConsole;
  }

  public send(entry: LogEntry): void {
    const levelName = LogLevel[entry.level];
    const consoleMessage = `[${entry.timestamp}] [${levelName}] [${entry.category}] ${entry.message}`;

    const isNode = typeof window === 'undefined';

    if (isNode) {
      // Clean Node.js string without CSS %c tags
      const dataStr = entry.data ? `\n${JSON.stringify(entry.data, null, 2)}` : '';
      const fullMessage = `${consoleMessage}${dataStr}`;

      switch (entry.level) {
      case LogLevel.TRACE:
      case LogLevel.DEBUG:
        this.nativeConsole.debug(fullMessage);
        break;
      case LogLevel.INFO:
      case LogLevel.SUCCESS:
        this.nativeConsole.info(fullMessage);
        break;
      case LogLevel.WARN:
        this.nativeConsole.warn(`YELLOW: ${fullMessage}`); // simple indicator
        break;
      case LogLevel.ERROR:
        this.nativeConsole.error(`RED: ${fullMessage}`);
        break;
      }
    } else {
      // Browser environment with %c CSS styling.
      // Spread `data` only when it's defined — passing `undefined` as a
      // third console.* arg renders the literal string "undefined" after
      // every message, which is exactly what the [BuildVersionCheck]
      // "tick undefined" log noise was.
      const styled = `%c${consoleMessage}`;
      const extra: unknown[] = entry.data === undefined ? [] : [entry.data];
      switch (entry.level) {
      case LogLevel.TRACE:
        this.nativeConsole.debug(styled, LOG_STYLES.TRACE, ...extra);
        break;
      case LogLevel.DEBUG:
        this.nativeConsole.debug(styled, LOG_STYLES.DEBUG, ...extra);
        break;
      case LogLevel.INFO:
        this.nativeConsole.info(styled, LOG_STYLES.INFO, ...extra);
        break;
      case LogLevel.SUCCESS:
        this.nativeConsole.info(styled, LOG_STYLES.SUCCESS, ...extra);
        break;
      case LogLevel.WARN:
        this.nativeConsole.warn(styled, LOG_STYLES.WARN, ...extra);
        break;
      case LogLevel.ERROR:
        this.nativeConsole.error(styled, LOG_STYLES.ERROR, ...extra);
        break;
      }
    }
  }
}

export interface RemoteLogContext {
  colyseusSessionId?: string;
  characterName?: string;
  characterClass?: string;
  userId?: string;
}

export interface RemoteTransportOptions {
  /** Lowest-severity entry shipped off-device. Defaults to WARN. */
  minimumLevel?: LogLevel;
  /** Hard memory bound while a request is in flight or the sink is slow. */
  maxBufferEntries?: number;
}

function sanitizeContext(context: RemoteLogContext): RemoteLogContext {
  const result: RemoteLogContext = {};
  if (typeof context.colyseusSessionId === 'string') {
    result.colyseusSessionId = redactLogString(context.colyseusSessionId).slice(0, 200);
  }
  if (typeof context.characterName === 'string') {
    result.characterName = redactLogString(context.characterName).slice(0, 128);
  }
  if (typeof context.characterClass === 'string') {
    result.characterClass = redactLogString(context.characterClass).slice(0, 64);
  }
  if (typeof context.userId === 'string' && isSafeLogUserId(context.userId)) {
    result.userId = context.userId.slice(0, 128);
  }
  return result;
}

export class RemoteTransport implements Transport {
  private remoteUrl: string;
  private sessionId: string;
  private buffer: LogEntry[] = [];
  private flushInterval: NodeJS.Timeout | null = null;
  private readonly BATCH_SIZE = 50;
  private readonly FLUSH_DELAY = 5000;
  private readonly minimumLevel: LogLevel;
  private readonly maxBufferEntries: number;
  private remoteLogFailed = false;
  private flushInFlight = false;
  private nativeConsole: Console;
  // G.3.1 (2026-05-14) — rich context included with every batch POST.
  // Stamped onto each ring entry server-side so MCP tools can group/join
  // by Colyseus sid, character, or user without separate fetches.
  private contextProvider: (() => RemoteLogContext) | null = null;

  constructor(
    url: string,
    sessionId: string,
    nativeConsole: Console,
    options: RemoteTransportOptions = {},
  ) {
    this.remoteUrl = url;
    this.sessionId = sessionId;
    this.nativeConsole = nativeConsole;
    this.minimumLevel = options.minimumLevel ?? LogLevel.WARN;
    this.maxBufferEntries = Math.max(this.BATCH_SIZE, options.maxBufferEntries ?? 200);
    this.startFlushTimer();
  }

  public setContextProvider(provider: (() => RemoteLogContext) | null): void {
    this.contextProvider = provider;
  }

  public send(entry: LogEntry): void {
    if (entry.level < this.minimumLevel || this.remoteLogFailed) {
      return;
    }

    // Logging must remain disposable under load. Keep the newest diagnostics
    // and place a hard ceiling on memory while a prior request is in flight.
    if (this.buffer.length >= this.maxBufferEntries) {
      this.buffer.shift();
    }
    this.buffer.push(entry);
    if (this.buffer.length >= this.BATCH_SIZE) {
      this.flush();
    }
  }

  public flush(): void {
    if (this.buffer.length === 0) return;

    // Circuit breaker: once the remote sink is known-unreachable (PTR has no
    // log endpoint / CORS-blocked), STOP attempting fetches. Pre-fix we only
    // suppressed our own console.error but kept firing fetch() every 5s and
    // every 50 buffered logs — so a per-frame error source (e.g. a lost WebGL
    // context throwing in update()) flooded the buffer to the 50-log batch
    // constantly, melting the console + network with NetworkError spam. Drop
    // the buffer and bail; logging stays local-only for the session.
    if (this.remoteLogFailed) {
      this.buffer.length = 0;
      return;
    }

    // Serialize POSTs. The previous implementation could launch a new fetch
    // for every 50 entries while older requests were unresolved.
    if (this.flushInFlight) {
      return;
    }

    const batch = this.buffer.splice(0, this.BATCH_SIZE);
    const sanitizedBatch = batch.map((entry) => LogSanitizer.sanitize(entry));

    let context: RemoteLogContext = {};
    try {
      context = sanitizeContext(this.contextProvider?.() ?? {});
    } catch {
      // provider crashes never break logging
    }
    const body = JSON.stringify({
      sessionId: this.sessionId,
      ...context,
      logs: sanitizedBatch,
    });
    this.flushInFlight = true;
    try {
      fetch(this.remoteUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
      }).then(response => {
        if (!response.ok) {
          throw new Error(`Remote log endpoint returned HTTP ${response.status}`);
        }
      }).catch(err => {
        if (!this.remoteLogFailed) {
          this.nativeConsole.error(
            'Failed to send remote logs to', this.remoteUrl,
            'bodyChars', body.length,
            '(suppressing further errors):', err,
          );
          this.remoteLogFailed = true;
        }
        this.buffer.length = 0;
      }).finally(() => {
        this.flushInFlight = false;
        if (!this.remoteLogFailed && this.buffer.length > 0) {
          this.flush();
        }
      });
    } catch (e) {
      this.flushInFlight = false;
      if (!this.remoteLogFailed) {
        this.nativeConsole.error('Exception during remote logging:', e);
        this.remoteLogFailed = true;
      }
      this.buffer.length = 0;
    }
  }

  private startFlushTimer(): void {
    if (!this.flushInterval) {
      this.flushInterval = setInterval(() => this.flush(), this.FLUSH_DELAY);
    }
  }

  public stop(): void {
    if (this.flushInterval) {
      clearInterval(this.flushInterval);
      this.flushInterval = null;
    }
    this.flush();
  }
}
